/**
 * Event & activity support and workshops (1.5.1, decisions of 1 Oct 2026).
 *
 * An **activity** is volunteer help with no tenant: the piano volunteer on Saturdays, bingo-night support, event
 * setup, prep, cooler duty. It has a name, what the volunteer does, where, a schedule (weekly on a weekday, or one
 * date), a cap (how many volunteers), the standing note "Check in with ABF staff at the front desk", and the
 * services it suits. Each date it runs is one SM_Events row (Audience Volunteers) made when the office presses Send
 * for that date; the volunteers' "I can help" presses are SM_EventRSVPs rows (one per volunteer — the unique RSVP
 * key); nothing is final until the office presses Confirm, never past the cap. An activity never touches
 * SM_Appointments, the slots or a cycle: it never counts toward Housekeeping. Volunteer hours on an activity are an
 * ABF record only (History, the monthly report) — never VISIONS.
 *
 * A **workshop** is an event: who runs it (FORA tech / staff / volunteer), day and time, place, details, a cap.
 * "Invite all tenants" queues the RSVP invitation once per language, every tenant with email in BCC, the Director
 * To and the co-administrator CC, through the Outbox. The tech (role `tech`) may propose one; it waits under Needs
 * your attention until staff publish it. The tech never sends anything.
 *
 * Pure rules shared by the API and the app. Nothing here reads or writes a list.
 */
import { z } from 'zod';
import { addDaysToKey, dayOfWeek, isDateKey } from './dates';
import { SERVICE_KEYS } from './volunteers';
import { MAX_ADDED_SERVICES } from './volunteer-services';
import type { AlmostView } from './volunteers-api';

/** The standing note on every activity (and in every E5 email). */
export const FRONT_DESK_NOTE = { en: 'Check in with ABF staff at the front desk.', es: 'Preséntese con el personal de ABF en la recepción.' } as const;

/** Volunteers on an activity: at most this many (the cap a person types is between 1 and this). */
export const ACTIVITY_CAP_MAX = 50;
/** How many coming dates the Events page shows for a weekly activity. */
export const ACTIVITY_DATES_SHOWN = 4;

const text = (max: number) => z.string().trim().max(max);
const dayKey = z.string().refine(isDateKey, 'date');
/** Weekends are open for volunteers and events: 7:00 am – 11:00 pm. */
const minutes = z.number().int().min(7 * 60).max(23 * 60);

/** A service's key: one of the six built-in, or an added one's slug. */
const serviceKey = z.string().trim().min(1).max(40);
/** Eli, 17 Oct 2026: Volunteer help → Other goes to every Ready volunteer, whatever they ticked. */
export const ALL_VOLUNTEERS = '__all';

export const ActivityScheduleSchema = z.discriminatedUnion('kind', [
  /** Weekly on a weekday (0 Sunday … 6 Saturday), from a first date, until a last date (blank = until stopped). */
  z.object({ kind: z.literal('weekly'), weekday: z.number().int().min(0).max(6), fromDayKey: dayKey, untilDayKey: dayKey.nullable().optional().default(null) }),
  z.object({ kind: z.literal('once'), dayKey }),
]);
export type ActivitySchedule = z.output<typeof ActivityScheduleSchema>;

export const ActivityInputSchema = z
  .object({
    name: text(120).min(1, 'required'),
    /** What the volunteer does. */
    what: text(1000).min(1, 'required'),
    /** D-1028-ONELANG-8: the Spanish wording of it, typed by the office (the app never translates). */
    whatEs: text(1000).optional().default(''),
    /** Where (a room in the building, or a place). */
    where: text(255).min(1, 'required'),
    schedule: ActivityScheduleSchema,
    minutes,
    /** How long, in minutes (30 min – 8 h). */
    duration: z.number().int().min(30).max(8 * 60),
    cap: z.number().int().min(1).max(ACTIVITY_CAP_MAX),
    // 1.8.3 (D-1.8.3-5): one of the six, or the key of a service the office added (the server checks it against the list)
    services: z.array(serviceKey).min(1, 'required').max(SERVICE_KEYS.length + MAX_ADDED_SERVICES),
  })
  .superRefine((a, ctx) => {
    if (a.schedule.kind === 'weekly') {
      if (a.schedule.untilDayKey && a.schedule.untilDayKey < a.schedule.fromDayKey) ctx.addIssue({ code: 'custom', path: ['schedule', 'untilDayKey'], message: 'after-start' });
    }
    if (a.minutes + a.duration > 24 * 60) ctx.addIssue({ code: 'custom', path: ['duration'], message: 'too-long' });
  });
export type ActivityInput = z.input<typeof ActivityInputSchema>;
export type ActivityParsed = z.output<typeof ActivityInputSchema>;

/** The coming dates of a schedule from a day (inclusive), at most `count`. */
export function activityDates(s: ActivitySchedule, fromKey: string, count: number): string[] {
  if (s.kind === 'once') return s.dayKey >= fromKey ? [s.dayKey] : [];
  let k = s.fromDayKey > fromKey ? s.fromDayKey : fromKey;
  while (dayOfWeek(k) !== s.weekday) k = addDaysToKey(k, 1);
  const out: string[] = [];
  while (out.length < count && (!s.untilDayKey || k <= s.untilDayKey)) {
    out.push(k);
    k = addDaysToKey(k, 7);
  }
  return out;
}

/** Is the day one the schedule runs on? */
export function runsOn(s: ActivitySchedule, k: string): boolean {
  if (s.kind === 'once') return s.dayKey === k;
  return dayOfWeek(k) === s.weekday && k >= s.fromDayKey && (!s.untilDayKey || k <= s.untilDayKey);
}

export type HelperState = 'helping' | 'standby' | 'confirmed';

export interface ActivityDateView {
  dayKey: string;
  /** The SM_Events row for the date, once Send was pressed (null before). */
  eventId: string | null;
  /** A closed day (its scope is "everything"): never offered. */
  closed: { reason: string } | null;
  sentTo: number;
  /** Who pressed "I can help", in the order they pressed; past the cap = standby; Confirm makes it final. */
  helpers: { volunteerId: string; name: string; at: string; state: HelperState }[];
  confirmed: number;
  /** Hours recorded (the close-out) for the confirmed volunteers. */
  closedOut: boolean;
  /** 1.8.3: the standby list in the order people joined (shared/standby.ts), with when the place was offered to each. */
  standby?: { volunteerId: string; name: string; joinedAt: string; offeredAt: string | null }[];
  /** 1.8.3: places still open (the number needed less everyone holding one). */
  placesLeft?: number;
}

export interface ActivityView {
  activityId: string;
  name: string;
  what: string;
  where: string;
  schedule: ActivitySchedule;
  minutes: number;
  duration: number;
  cap: number;
  /** The six built-in keys, or (1.8.3) the key of a service the office added. */
  services: string[];
  /** 1.8.3: the services in words, in the order of `services` (an added one by the names the office typed). */
  serviceWords?: { en: string; es: string }[];
  /** 1.8.3: made from Book → Volunteer help. */
  help?: boolean;
  /** 1.8.3: a tenant named on the help — the office's own record only; never in anything a volunteer receives. */
  forTenant?: { tenantId: string; unit: string; name: string } | null;
  note: string;
  createdBy: string;
  createdAt: string;
  stopped: boolean;
  dates: ActivityDateView[];
}

export interface ActivitiesResponse {
  activities: ActivityView[];
  /** Today's confirmed activities (Today's "Activities today" block). */
  today: { activityId: string; eventId: string; name: string; where: string; minutes: number; duration: number; volunteers: string[] }[];
}

export const ActivitySendSchema = z.object({ dayKey, audience: z.enum(['available', 'all']) });
export const ActivityConfirmSchema = z.object({ dayKey, volunteerId: z.string().trim().min(3).max(50) });
/** 1.8.3: Offer to the next on standby (a date), and Can't make it (a volunteer on a date). */
export const ActivityOfferSchema = z.object({ dayKey });
export const ActivityReleaseSchema = z.object({ dayKey, volunteerId: z.string().trim().min(3).max(50) });

/**
 * 1.8.3 (D-1.8.3-5) — Book → Volunteer help: the service, what it is, where, the day and time, how many are needed,
 * and how it is filled: **assign** a volunteer already arranged (confirmed at once; one confirmation email) or
 * **send** to every Ready volunteer who has the service (the first to press "I can help" up to the number needed,
 * then the standby question). A tenant may be named — the office's own record only.
 */
export const VolunteerHelpInputSchema = z
  .object({
    service: serviceKey,
    /** What it is, in a few words ("Piano in the lobby") — the volunteer reads it. */
    name: text(120).min(1, 'required'),
    /** What the volunteer does (optional; the name stands when empty). */
    details: text(1000).optional().default(''),
    /** D-1101-SMALL-1: the Spanish wording of the details (or of the name when there are no details), typed by the office; never translated. */
    detailsEs: text(1000).optional().default(''),
    where: text(255).min(1, 'required'),
    schedule: ActivityScheduleSchema,
    minutes,
    duration: z.number().int().min(30).max(8 * 60),
    needed: z.number().int().min(1).max(ACTIVITY_CAP_MAX),
    tenant: z.object({ tenantId: text(60).min(1), unit: text(40), name: text(160) }).nullable().optional().default(null),
    how: z.discriminatedUnion('kind', [z.object({ kind: z.literal('assign'), volunteerId: z.string().trim().min(3).max(50) }), z.object({ kind: z.literal('send') })]),
  })
  .superRefine((a, ctx) => {
    if (a.schedule.kind === 'weekly' && a.schedule.untilDayKey && a.schedule.untilDayKey < a.schedule.fromDayKey) ctx.addIssue({ code: 'custom', path: ['schedule', 'untilDayKey'], message: 'after-start' });
    if (a.minutes + a.duration > 24 * 60) ctx.addIssue({ code: 'custom', path: ['duration'], message: 'too-long' });
  });
export type VolunteerHelpInput = z.input<typeof VolunteerHelpInputSchema>;

/** Who has a service ticked, Ready first; a not-Ready one says what is missing and cannot be sent to or assigned. */
export interface HelpPickerView {
  service: string;
  serviceWords: { en: string; es: string };
  volunteers: { volunteerId: string; name: string; ready: boolean; missing: string[]; hasEmail: boolean }[];
  ready: number;
  /** Eli, 21 Oct: ticked but not Ready (not inactive, not opted out) — the staff screen's "who to chase" line; never listed to pick, never sent to. */
  almost: AlmostView[];
}

export interface VolunteerHelpResult {
  activity: ActivityView;
  dayKey: string;
  how: 'assign' | 'send';
  queued: number;
  message: string;
}

export const ActivityHoursSchema = z.object({
  dayKey,
  /** Minutes each confirmed volunteer gave (blank = the scheduled length). */
  entries: z.array(z.object({ volunteerId: z.string().trim().min(3).max(50), came: z.boolean(), minutes: z.number().int().min(0).max(16 * 60).nullable() })).max(ACTIVITY_CAP_MAX),
});

/* ------------------------------------------------------------------------------------------------
 * Workshops
 * ---------------------------------------------------------------------------------------------- */

export const WORKSHOP_RUNNERS = ['tech', 'staff', 'volunteer'] as const;
export type WorkshopRunner = (typeof WORKSHOP_RUNNERS)[number];

export const WorkshopInputSchema = z.object({
  title: text(120).min(1, 'required'),
  runBy: z.enum(WORKSHOP_RUNNERS),
  /** The name of the person who runs it (optional). */
  runnerName: text(120).optional().default(''),
  dayKey,
  minutes,
  endMinutes: minutes.nullable().optional().default(null),
  place: text(255).min(1, 'required'),
  details: text(2000).optional().default(''),
  /** D-1028-ONELANG-8: the Spanish wording of the note, typed by the office (the app never translates). */
  detailsEs: text(2000).optional().default(''),
  cap: z.number().int().min(1).max(500),
  /** Set when the workshop comes from a tech's proposal. */
  proposalId: z.string().regex(/^WSP-\d{8}-[A-F0-9]{6}$/).nullable().optional().default(null),
});
export type WorkshopInput = z.input<typeof WorkshopInputSchema>;

/** What the tech may propose: a title, what it is, when. Nothing else, and nothing is sent. */
export const WorkshopProposalSchema = z.object({
  title: text(120).min(1, 'required'),
  what: text(1000).min(1, 'required'),
  dayKey,
  minutes,
});
export type WorkshopProposalInput = z.input<typeof WorkshopProposalSchema>;

export interface WorkshopProposalView {
  proposalId: string;
  title: string;
  what: string;
  dayKey: string;
  minutes: number;
  proposedBy: string;
  proposedAt: string;
  state: 'waiting' | 'published' | 'declined';
  eventId: string | null;
}

/** 1.8.5 (D-1.8.5-2): one line of Tech Support → Workshops. */
export interface WorkshopRowView {
  eventId: string;
  title: string;
  dayKey: string;
  minutes: number | null;
  place: string;
  cap: number;
  runBy: WorkshopRunner;
  runnerName: string;
  status: string;
  invitedTenants: number;
  lastInvite: string | null;
  /** The tech's own (made by him or from his proposal). */
  mine: boolean;
  /** Invite all tenants is open to this person now (ahead, not cancelled; the tech only for his own). */
  canInvite: boolean;
  past: boolean;
}

export interface WorkshopInviteResult {
  queued: number;
  tenants: number;
  noEmail: number;
  /** 1.5.4: tenants who asked for no event or invitation emails ("Stop these emails") — left out. */
  noMarketing: number;
  sendAt: string | null;
  message: string;
}
